import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Shield as ShieldIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import DataTable, { Column } from '../../../components/Common/DataTable';
import { settingsService, Profile } from '../../../services';

const Profiles: React.FC = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [formData, setFormData] = useState({
    description: '',
    code: '',
  });

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsService.getProfiles();
      setProfiles(response.data || []);
    } catch (err: any) {
      console.error('Error fetching profiles:', err);
      setError(err.message || 'Erro ao carregar perfis');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleOpenDialog = (profile?: Profile) => {
    if (profile) {
      setSelectedProfile(profile);
      setFormData({
        description: profile.description,
        code: profile.code,
      });
    } else {
      setSelectedProfile(null);
      setFormData({
        description: '',
        code: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
    setFormData({
      description: '',
      code: '',
    });
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (selectedProfile) {
        // Update
        await settingsService.updateProfile(selectedProfile.id, formData);
      } else {
        // Create
        await settingsService.createProfile(formData);
      }
      handleCloseDialog();
      fetchProfiles();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar perfil');
    }
  };

  const handleDelete = async (profile: Profile) => {
    if (!window.confirm(`Tem certeza que deseja eliminar o perfil "${profile.description}"?`)) {
      return;
    }

    try {
      setError(null);
      await settingsService.deleteProfile(profile.id);
      fetchProfiles();
    } catch (err: any) {
      setError(err.message || 'Erro ao eliminar perfil');
    }
  };

  const columns: Column<Profile>[] = [
    {
      id: 'code',
      label: 'Código',
      minWidth: 100,
    },
    {
      id: 'description',
      label: 'Descrição',
      minWidth: 200,
    },
  ];

  if (loading && profiles.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Perfis de Utilizador
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestão de perfis e permissões de acesso
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchProfiles}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Perfil
          </Button>
        </Box>
      </Box>

      {/* Stats Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.light',
                color: 'primary.main',
              }}
            >
              <ShieldIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {profiles.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Perfis Cadastrados
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={profiles}
        loading={loading}
        onRefresh={fetchProfiles}
        onEdit={handleOpenDialog}
        onDelete={handleDelete}
        rowKey="id"
        emptyMessage="Nenhum perfil encontrado"
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedProfile ? 'Editar Perfil' : 'Novo Perfil'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Código"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="Ex: PFADM"
              disabled={!!selectedProfile}
              helperText="Código único do perfil (não pode ser alterado)"
            />
            <TextField
              fullWidth
              label="Descrição"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Ex: Administrador"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.code || !formData.description}
          >
            {selectedProfile ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profiles;
